/**
 * PricePerItem custom element.
 *
 * Displays the applicable unit price next to the quantity input for products
 * with volume pricing. Updates dynamically as the quantity changes.
 *
 * Two rendering contexts:
 *
 *  Product page  (.product-form__quantity parent)
 *    total qty = data-cart-quantity (already in cart) + input.value (to add)
 *    data-cart-quantity must be kept in sync with the live cart — see below.
 *
 *  Cart page  (.cart-item__quantity parent)
 *    total qty = input.value only (the input already IS the cart quantity;
 *    data-cart-quantity is absent so it defaults to 0, giving the right sum).
 *    After any cart mutation CartItems replaces .js-contents with fresh HTML,
 *    destroying and recreating each element — no listener needed here.
 *
 * Synchronisation on the product page
 * ------------------------------------
 * Megastore dispatches a custom "cart:change" document event (with
 * event.detail.cart containing the full Shopify cart JSON) from both
 * product-form.js (after add-to-cart) and cart.js (after qty change /
 * removal in the cart or drawer).  We listen to this event and update
 * data-cart-quantity so that _getCurrentQuantity() stays accurate.
 */
if (!customElements.get("price-per-item")) {
	class PricePerItem extends HTMLElement {
		connectedCallback() {
			this._priceBreaks = JSON.parse(this.dataset.priceBreaks || "[]");
			this._prefix = this.dataset.prefix || "";
			this._suffix = this.dataset.suffix || "";
			this._priceText = this.querySelector(".price-per-item__text");

			// Locate the sibling quantity input — works for both contexts.
			const wrapper = this.closest(
				".product-form__quantity, .cart-item__quantity"
			);
			this._quantityInput = wrapper
				? wrapper.querySelector(".quantity__input")
				: null;

			// True only when rendered inside the product form on a product page.
			this._isProductPageContext = !!this.closest(".product-form__quantity");

			if (this._quantityInput) {
				this._handleChange = this._updatePrice.bind(this);
				this._quantityInput.addEventListener("change", this._handleChange);
				this._quantityInput.addEventListener("input", this._handleChange);
				this._updatePrice(); // set correct initial display
			}

			// On the product page, data-cart-quantity must be refreshed whenever the
			// cart changes (add-to-cart, cart-page qty change, drawer update, removal).
			// The cart page handles its own updates via full section re-renders.
			if (this._isProductPageContext) {
				this._handleCartChange = this._onCartChange.bind(this);
				document.addEventListener("cart:change", this._handleCartChange);
			}
		}

		disconnectedCallback() {
			if (this._quantityInput && this._handleChange) {
				this._quantityInput.removeEventListener("change", this._handleChange);
				this._quantityInput.removeEventListener("input", this._handleChange);
			}
			if (this._handleCartChange) {
				document.removeEventListener("cart:change", this._handleCartChange);
			}
		}

		// ─── cart:change handler (product page only) ───────────────────────────

		_onCartChange(event) {
			const cart = event.detail?.cart;
			if (!cart?.items || !this._quantityInput) return;

			// The variant being sold is stored in the product form's hidden id input,
			// which is kept current by VariantSelects.updateVariantInput() on variant
			// switches and by the product form on add-to-cart.
			const productForm = this.closest("product-form");
			const variantId = productForm?.querySelector('input[name="id"]')?.value;
			if (!variantId) return;

			const cartItem = cart.items.find(
				(item) => String(item.variant_id) === String(variantId)
			);

			// New quantity already in cart (0 if the item was removed).
			const newCartQty = cartItem ? cartItem.quantity : 0;

			// Persist on the input so _getCurrentQuantity() adds it to input.value.
			this._quantityInput.setAttribute("data-cart-quantity", String(newCartQty));

			this._updatePrice();
		}

		// ─── quantity helpers ──────────────────────────────────────────────────

		_getCurrentQuantity() {
			if (!this._quantityInput) return 1;

			// Product page: data-cart-quantity holds what is already in the cart;
			//   we add the input value (quantity the user intends to add).
			// Cart page: attribute is absent → 0, so total = input.value alone,
			//   which already equals the current cart line quantity.
			const cartQty =
				parseInt(this._quantityInput.dataset.cartQuantity || "0") || 0;
			const inputQty = parseInt(this._quantityInput.value) || 1;
			return cartQty + inputQty;
		}

		_updatePrice() {
			if (!this._priceBreaks.length || !this._priceText) return;

			const qty = this._getCurrentQuantity();

			// Price breaks are sorted descending by quantity; find the first tier
			// whose minimum threshold the current quantity meets.
			const tier =
				this._priceBreaks.find((pb) => qty >= pb.quantity) ??
				this._priceBreaks[this._priceBreaks.length - 1];

			if (tier) {
				this._priceText.textContent = this._prefix + tier.price + this._suffix;
			}
		}
	}

	customElements.define("price-per-item", PricePerItem);
}
